/**
 * Per-user notifications API client (mttr/app_api/routes/notifications.py).
 * All endpoints scoped to the caller's own data; token sent via the
 * gen5-pwa `access-token` header convention.
 */

import { ApiError, apiFetch } from "./apiClient";

export type ChannelKind = "email" | "sms";
export type AlertType = "refill" | "manual_checkout" | "outage";
export type Cadence = "immediate" | "daily" | "weekly" | "monthly";

export interface NotificationChannel {
  channel_id: number;
  kind: ChannelKind;
  address: string;
  label: string | null;
  created_utc: string | null;
}

export interface NotificationPreference {
  preference_id: number;
  alert_type: AlertType;
  channel_id: number;
  channel_kind: ChannelKind;
  channel_address: string;
  cadence: Cadence;
  hour_local: number;
  minute_local: number;
  /** 7-bit mask: Sun=1, Mon=2, Tue=4, Wed=8, Thu=16, Fri=32, Sat=64. */
  dow_mask: number;
  /** Day of month (1-28) for cadence='monthly'. */
  day_of_month: number;
  /** Which SmartBox this subscription reports on. Required + set for
   *  manual_checkout; null for refill (which is scoped to the user's boxes). */
  scu: string | null;
  enabled: boolean;
  last_fired_utc: string | null;
  last_fire_status: string | null;
  created_utc: string | null;
}

export interface NotificationLogEntry {
  log_id: number;
  preference_id: number | null;
  alert_type: string;
  channel_kind: ChannelKind;
  address: string;
  subject: string | null;
  body_preview: string;
  provider: string | null;
  status: "sent" | "error" | "suppressed";
  error: string | null;
  sent_utc: string | null;
}

export class NotificationsApiError extends Error {
  constructor(message: string, readonly status?: number) {
    super(message);
    this.name = "NotificationsApiError";
  }
}

async function request<T>(
  method: string,
  path: string,
  token: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = { method };
  if (body !== undefined) init.body = JSON.stringify(body);
  try {
    return await apiFetch<T>(path, token, init);
  } catch (e) {
    throw e instanceof ApiError ? new NotificationsApiError(e.message, e.status) : e;
  }
}

// ----- channels -----------------------------------------------------------

export async function fetchChannels(
  token: string,
): Promise<NotificationChannel[]> {
  const r = await request<{ channels: NotificationChannel[] }>(
    "GET", "/api/notifications/channels", token,
  );
  return r.channels;
}

export function createChannel(
  token: string,
  body: { kind: ChannelKind; address: string; label?: string | null },
): Promise<NotificationChannel> {
  return request<NotificationChannel>(
    "POST", "/api/notifications/channels", token, body,
  );
}

export function deleteChannel(
  token: string,
  channelId: number,
): Promise<{ channel_id: number; deleted: boolean }> {
  return request("DELETE", `/api/notifications/channels/${channelId}`, token);
}

// ----- preferences --------------------------------------------------------

export async function fetchPreferences(
  token: string,
): Promise<NotificationPreference[]> {
  const r = await request<{ preferences: NotificationPreference[] }>(
    "GET", "/api/notifications/preferences", token,
  );
  return r.preferences;
}

export function createPreference(
  token: string,
  body: {
    alert_type: AlertType;
    channel_id: number;
    cadence: Cadence;
    /** Optional for cadence='immediate' (ignored server-side). */
    hour_local?: number;
    minute_local?: number;
    dow_mask?: number;
    /** Required for alert_type='manual_checkout'; omit for 'refill'. */
    scu?: string | null;
    /** For cadence='monthly' (1-28). */
    day_of_month?: number;
  },
): Promise<NotificationPreference> {
  return request<NotificationPreference>(
    "POST", "/api/notifications/preferences", token, body,
  );
}

export function updatePreference(
  token: string,
  preferenceId: number,
  patch: Partial<{
    enabled: boolean;
    cadence: Cadence;
    hour_local: number;
    minute_local: number;
    dow_mask: number;
    channel_id: number;
    day_of_month: number;
    scu: string;
  }>,
): Promise<NotificationPreference> {
  return request<NotificationPreference>(
    "POST",
    `/api/notifications/preferences/${preferenceId}`,
    token,
    patch,
  );
}

export function deletePreference(
  token: string,
  preferenceId: number,
): Promise<{ preference_id: number; deleted: boolean }> {
  return request("DELETE", `/api/notifications/preferences/${preferenceId}`, token);
}

// ----- log ----------------------------------------------------------------

export async function fetchNotificationLog(
  token: string,
  limit = 20,
): Promise<NotificationLogEntry[]> {
  const r = await request<{ entries: NotificationLogEntry[] }>(
    "GET", `/api/notifications/log?limit=${limit}`, token,
  );
  return r.entries;
}

// ----- helpers ------------------------------------------------------------

export const DOW_BITS: { bit: number; label: string }[] = [
  { bit: 1,  label: "Sun" },
  { bit: 2,  label: "Mon" },
  { bit: 4,  label: "Tue" },
  { bit: 8,  label: "Wed" },
  { bit: 16, label: "Thu" },
  { bit: 32, label: "Fri" },
  { bit: 64, label: "Sat" },
];

/** Render a dow_mask back to a human label ("Daily", "Mon, Wed, Fri", etc.). */
export function dowMaskLabel(mask: number): string {
  if (mask === 127) return "Every day";
  const days = DOW_BITS.filter((d) => mask & d.bit).map((d) => d.label);
  return days.length > 0 ? days.join(", ") : "(no days)";
}

/** "09:00" from (9, 0). */
export function timeLabel(hour: number, minute: number): string {
  const h = String(hour).padStart(2, "0");
  const m = String(minute).padStart(2, "0");
  return `${h}:${m}`;
}
